import { AppVersionVo } from './app-version-vo';
import { DateVo } from './date-vo';
import { MultiTemplateModel } from './multi-template-model';
import { SlotPathRenderMode } from './slot-path-render-mode';
import { SuitablePhotosCountVo } from './suitable-photos-count-vo';
import { TemplateModel } from './template-model';
import { TemplateSizeCateoryEnum } from './template-size-category-enum';

export type SlotScrapInfo = {
  id: string;
  size: { width: number; height: number };
  scale: { width: number; height: number };
  angle: number;
  center: { x: number; y: number };
  slot: {
    path: string | null;
    pathRenderMode: SlotPathRenderMode | null;
    size: { width: number; height: number };
    center: { x: number; y: number };
  };
  decoOverlay:
    | {
        size: { width: number; height: number };
        center: { x: number; y: number };
        imageUrl: string;
        sourceUrl: string;
      }
    | undefined;
};

export class SingleTemplateModel extends TemplateModel {
  constructor(
    id: number,
    author: string,
    uploaderInfo: string,
    sourceUrl: string,
    isVip: boolean,
    isAnimated: boolean,
    thumbnailUrl: string,
    animatedThumbnailUrl: string,
    sizeCategory: TemplateSizeCateoryEnum,
    slotsCount: number,
    createdAt: DateVo,
    updatedAt: DateVo,
    isMultipage: boolean,
    isAdjustable: boolean,
    creatorId: string | null,
    suitablePhotosCount: SuitablePhotosCountVo,
    labels: string[],
    minimalRequiredVersionIos: AppVersionVo | null,
    minimalRequiredVersionAndroid: AppVersionVo | null,
    templateTagIds: number[],
    shouldRemoveAnimatedThumbnail: boolean,
    thumbnailBinary: Uint8Array | null,
    animatedThumbnailBinary: Uint8Array | null,
    newTemplateTagNames: string[],
    suitsVideo: boolean,
    private collageStruct: any,
  ) {
    super(
      id,
      author,
      uploaderInfo,
      sourceUrl,
      isVip,
      isAnimated,
      thumbnailUrl,
      animatedThumbnailUrl,
      sizeCategory,
      slotsCount,
      creatorId,
      createdAt,
      updatedAt,
      isMultipage,
      isAdjustable,
      suitablePhotosCount,
      labels,
      minimalRequiredVersionIos,
      minimalRequiredVersionAndroid,
      templateTagIds,
      shouldRemoveAnimatedThumbnail,
      thumbnailBinary,
      animatedThumbnailBinary,
      newTemplateTagNames,
      suitsVideo,
    );
  }

  public static create(
    id: number,
    author: string,
    uploaderInfo: string,
    sourceUrl: string,
    isVip: boolean,
    isAnimated: boolean,
    imageUrl: string,
    animatedImageUrl: string,
    sizeCategory: TemplateSizeCateoryEnum,
    slotsCount: number,
    createdAt: DateVo,
    updatedAt: DateVo,
    isMultipage: boolean,
    isAdjustable: boolean,
    creatorId: string | null,
    suitablePhotosCount: SuitablePhotosCountVo,
    labels: string[],
    minimalRequiredVersionIos: AppVersionVo | null,
    minimalRequiredVersionAndroid: AppVersionVo | null,
    templateTagIds: number[],
    shouldRemoveAnimatedThumbnail: boolean,
    thumbnailBinary: Uint8Array | null,
    animatedThumbnailBinary: Uint8Array | null,
    newTemplateTagNames: string[],
    suitsVideo: boolean,
    collageStruct: any,
  ): SingleTemplateModel {
    return new SingleTemplateModel(
      id,
      author,
      uploaderInfo,
      sourceUrl,
      isVip,
      isAnimated,
      imageUrl,
      animatedImageUrl,
      sizeCategory,
      slotsCount,
      createdAt,
      updatedAt,
      isMultipage,
      isAdjustable,
      creatorId,
      suitablePhotosCount,
      labels,
      minimalRequiredVersionIos,
      minimalRequiredVersionAndroid,
      templateTagIds,
      shouldRemoveAnimatedThumbnail,
      thumbnailBinary,
      animatedThumbnailBinary,
      newTemplateTagNames,
      suitsVideo,
      collageStruct,
    );
  }

  public static createMock(): SingleTemplateModel {
    return new SingleTemplateModel(
      0,
      '',
      '',
      '',
      false,
      false,
      '',
      '',
      TemplateSizeCateoryEnum.Square,
      0,
      DateVo.create(new Date()),
      DateVo.create(new Date()),
      false,
      false,
      '',
      SuitablePhotosCountVo.create(0, 0),
      [],
      null,
      null,
      [],
      false,
      null,
      null,
      [],
      false,
      null,
    );
  }

  public getCollageStruct() {
    return this.collageStruct;
  }

  public getStickerScrapInfos(): { id: string; imageUrl: string; isFrozen: boolean }[] {
    return this.getStickerScraps().map((stickerScrap: any) => ({
      id: stickerScrap.scrap_id,
      imageUrl: `/api/assets?name=${stickerScrap.image?.source_url}`,
      isFrozen: stickerScrap.is_frozen,
    }));
  }

  public getSlots() {
    return this.collageStruct?.grid?.slots ?? [];
  }

  public getSlotInfos(): {
    index: number;
    path: string | null;
    pathRenderMode: SlotPathRenderMode | null;
    size: { width: number; height: number };
    position: { x: number; y: number };
  }[] {
    const slots = this.getSlots();

    return slots.map((slot: any, slotIndex: number) => {
      const width = slot.rect[1][0] * this.getSize().width;
      const height = slot.rect[1][1] * this.getSize().height;

      const size = { width, height };
      const position = {
        x: slot.rect[0][0] * this.getSize().width + size.width * 0.5,
        y: slot.rect[0][1] * this.getSize().height + size.height * 0.5,
      };

      return {
        index: slotIndex,
        path: slot.path ?? null,
        pathRenderMode: slot.path_render_mode ?? null,
        size,
        position,
      };
    });
  }

  public updateSlotPathRenderMode(slotIndex: number, pathRenderMode: SlotPathRenderMode | null): void {
    const slots = this.getSlots();
    // @ts-expect-error
    const slotToUpdate = slots.find((_, index) => index === slotIndex);

    if (slotToUpdate) {
      if (pathRenderMode) {
        slotToUpdate.path_render_mode = pathRenderMode;
      } else {
        delete slotToUpdate.path_render_mode;
      }
    }

    this.collageStruct = { ...this.collageStruct };
  }

  public getSlotScrapInfos(): SlotScrapInfo[] {
    return this.getSlotScraps().map((slotScrap: any) => {
      const widthScale = slotScrap.transform.scale?.[0] || 1;
      const heightScale = slotScrap.transform.scale?.[1] || 1;

      const frameWidth = slotScrap.frame.size[0];
      const frameHeight = slotScrap.frame.size[1];

      const slotWidth = slotScrap.slot.rect[1][0] * frameWidth;
      const slotHeight = slotScrap.slot.rect[1][1] * frameHeight;

      return {
        id: slotScrap.scrap_id,
        slot: {
          path: slotScrap.slot.path ?? null,
          pathRenderMode: slotScrap.slot.path_render_mode ?? null,
          size: {
            width: slotWidth,
            height: slotHeight,
          },
          center: {
            x: frameWidth * slotScrap.slot.rect[0][0] + slotWidth * 0.5,
            y: frameHeight * slotScrap.slot.rect[0][1] + slotHeight * 0.5,
          },
        },
        decoOverlay: slotScrap.deco_overlay
          ? {
              size: {
                width: slotScrap.deco_overlay.rect[1][0] * frameWidth,
                height: slotScrap.deco_overlay.rect[1][1] * frameHeight,
              },
              center: {
                x:
                  frameWidth * slotScrap.deco_overlay.rect[0][0] + slotScrap.deco_overlay.rect[1][0] * frameWidth * 0.5,
                y:
                  frameHeight * slotScrap.deco_overlay.rect[0][1] +
                  slotScrap.deco_overlay.rect[1][1] * frameHeight * 0.5,
              },
              sourceUrl: slotScrap.deco_overlay.source_url,
              imageUrl: `/api/assets?name=${slotScrap.deco_overlay.source_url}`,
            }
          : undefined,
        size: {
          width: frameWidth,
          height: frameHeight,
        },
        scale: {
          width: widthScale,
          height: heightScale,
        },
        angle: slotScrap.transform.angle,
        center: {
          x: slotScrap.frame.center[0],
          y: slotScrap.frame.center[1],
        },
      };
    });
  }

  public getTextScrapInfos(): { text: string; fontName: string }[] {
    return this.getTextScraps().map((textScrap: any) => {
      return {
        text: textScrap?.text?.text || '',
        fontName: textScrap?.text?.text_format?.font?.font_name || '',
      };
    });
  }

  public getPhotoScrapInfos(): { id: string; imageUrl: string; isFrozen: boolean }[] {
    return this.getPhotoScraps().map((photoScrap: any) => ({
      id: photoScrap.scrap_id,
      imageUrl: `/api/assets?name=${photoScrap.image?.source_url}`,
      isFrozen: photoScrap.is_frozen,
    }));
  }

  public getBackgroundScrapInfos(): { id: string; imageUrl: string }[] {
    return this.getBackgrounScraps().map((background: any) => ({
      id: background.scrap_id,
      imageUrl: `/api/assets?name=${background.image?.source_url}`,
    }));
  }

  public getBackgroundInfo() {
    return this.collageStruct.background;
  }

  public getAnimationInfo() {
    return this.collageStruct.default_page_animation;
  }

  public getRecipeInfo() {
    return this.collageStruct.recipe;
  }

  public updateStickerScrapInfo(id: string, isFrozen: boolean): void {
    const stickerScrapToUpdate = this.collageStruct.scraps.find(
      (scrap: any) => scrap.scrap_type === 'image' && scrap.is_sticker && scrap.scrap_id === id,
    );

    if (stickerScrapToUpdate) {
      stickerScrapToUpdate.is_frozen = isFrozen;
    }

    this.collageStruct = { ...this.collageStruct };
  }

  private getImageScraps() {
    return this.collageStruct.scraps.filter((scrap: any) => scrap.scrap_type === 'image');
  }

  private getStickerScraps() {
    return this.getImageScraps().filter((scrap: any) => scrap.is_sticker);
  }

  private getSlotScraps() {
    return this.collageStruct.scraps.filter((scrap: any) => scrap.scrap_type === 'slot');
  }

  private getPhotoScraps() {
    return this.getImageScraps().filter((scrap: any) => !scrap.is_sticker && !scrap.is_background);
  }

  private getBackgrounScraps() {
    return this.getImageScraps().filter((scrap: any) => scrap.is_background);
  }

  private getTextScraps() {
    return this.collageStruct.scraps.filter((scrap: any) => scrap.scrap_type === 'text');
  }

  public getSize(): { width: number; height: number } {
    return {
      width: this.collageStruct.width,
      height: this.collageStruct.height,
    };
  }

  public clone(): SingleTemplateModel {
    return new SingleTemplateModel(
      this.getId(),
      this.getAuthor(),
      this.getUploaderInfo(),
      this.getSourceUrl(),
      this.getIsVip(),
      this.getIsAnimated(),
      this.getThumbnailUrl(),
      this.getAnimatedThumbnailUrl(),
      this.getSizeCategory(),
      this.getSlotsCount(),
      this.getCreatedAt(),
      this.getUpdatedAt(),
      this.getIsMultipage(),
      this.getIsAdjustable(),
      this.getCreatorId(),
      this.getSuitablePhotosCount(),
      this.getLabels(),
      this.getMinimalRequiredVersionIos(),
      this.getMinimalRequiredVersionAndroid(),
      this.getTemplateTagIds(),
      this.getShouldRemoveAnimatedThumbnail(),
      this.getThumbnailBinary(),
      this.getAnimatedThumbnailBinary(),
      this.getNewTemplateTagNames(),
      this.getSuitsVideo(),
      JSON.parse(JSON.stringify(this.collageStruct)),
    );
  }

  public isSingleTemplateModel(): this is SingleTemplateModel {
    return true;
  }

  public isMultiTemplateModel(): this is MultiTemplateModel {
    return false;
  }
}
